import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

/**
 * Monochrome, sober button system (black & white).
 * Keep sizes; variants tuned for neutral palette.
 */

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-colors",
  {
    variants: {
      variant: {
        // Primary: black solid
        default:
          "bg-black text-white hover:bg-neutral-900 active:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-400",
        // Secondary: white with neutral border
        secondary:
          "bg-white text-neutral-900 border border-neutral-300 hover:bg-neutral-50 active:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-300",
        // Outline: transparent with border
        outline:
          "bg-transparent text-neutral-900 border border-neutral-300 hover:bg-neutral-50 active:bg-neutral-100",
        // Ghost: text only
        ghost:
          "bg-transparent text-neutral-800 hover:bg-neutral-100 active:bg-neutral-200",
        // Link style
        link:
          "bg-transparent underline underline-offset-4 text-neutral-900 hover:no-underline",
        // Destructive stays red for clarity (optional: switch to black if you truly want monochrome)
        destructive:
          "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-400",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
)

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
